import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, catchError, timeout } from 'rxjs';
import { ENV_CONFIG } from '../../environments/environment.config';
import { TIMEOUT_CONFIG } from '../constants/api.constants';
import {
    AbandonActivationResponse,
    ActivacionErrorCode,
    ActivacionErrorResponse,
    GetActivationStatusResponse,
    GetCountryConfigResponse,
    StartActivationRequest,
    StartActivationResponse,
    StartWorkflowResponse,
} from '../types/activacion-prepago.model';
import {
    MaxActivationsCheckRequest,
    MaxActivationsCheckResponse,
    MaxActivationsError,
} from '../types/max-activations.model';
import {
    GetCatalogEntriesRequest,
    GetCatalogEntriesResponse,
    GetCatalogLevelsResponse,
    ManualEntryRequest,
    ManualEntryResponse,
    ManualEntryError,
} from '../types/catalog.model';
import { LoggerService } from './logger.service';
import {
    OcrRequest,
    OcrResponse,
    OcrError,
    OcrErrorCode,
} from '../types/document-capture.model';

/**
 * Servicio HTTP para el módulo de Activaciones Prepago.
 *
 * Endpoints:
 * - POST   /activations                — Inicia una nueva activación (startActivation)
 * - GET    /activations/:id            — Consulta el estado de una activación (getStatus)
 * - DELETE /activations/:id            — Abandona una activación en curso (abandon)
 */
@Injectable({
    providedIn: 'root'
})
export class ActivacionHttpService {
    private http = inject(HttpClient);
    private logger = inject(LoggerService);
    private readonly BASE_URL = ENV_CONFIG.api.activaciones;
    private readonly IDENTITY_URL = ENV_CONFIG.api.activacionesIdentity;

    // ── Headers ───────────────────────────────────────────────────────────────

    private getAuthHeaders(): HttpHeaders {
        const token = localStorage.getItem('accessToken');
        return new HttpHeaders({
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        });
    }

    // ── Start Activation ──────────────────────────────────────────────────────

    /**
     * Inicia una nueva sesión de activación prepago.
     * Valida unicidad del vendedor, consulta saldo POS, obtiene pasos del workflow
     * y arranca la ejecución de Step Functions.
     *
     * POST /activations
     */
    startActivation(request: StartActivationRequest): Observable<StartActivationResponse> {
        const url = `${this.BASE_URL}${ENV_CONFIG.endpoints.activaciones.start}`;

        return this.http.post<StartActivationResponse>(url, request, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleError.bind(this))
        );
    }

    // ── Get Country Config ────────────────────────────────────────────────────

    /**
     * Obtiene la configuración del país: tipos de documento y pasos del workflow.
     * Se usa para poblar el dropdown de tipos de documento al iniciar el flujo.
     *
     * GET /config/:countryCode
     */
    getCountryConfig(countryCode: string): Observable<GetCountryConfigResponse> {
        const url = `${this.BASE_URL}${ENV_CONFIG.endpoints.activaciones.config(countryCode)}`;

        return this.http.get<GetCountryConfigResponse>(url, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleError.bind(this))
        );
    }

    // ── Get Status ────────────────────────────────────────────────────────────

    /**
     * Consulta el estado actual de una activación.
     * Combina el estado local de la sesión con el estado real de Step Functions.
     *
     * GET /activations/:id
     */
    getActivationStatus(activationId: string): Observable<GetActivationStatusResponse> {
        const url = `${this.BASE_URL}${ENV_CONFIG.endpoints.activaciones.getStatus(activationId)}`;

        return this.http.get<GetActivationStatusResponse>(url, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleError.bind(this))
        );
    }

    // ── Abandon ───────────────────────────────────────────────────────────────

    /**
     * Abandona una activación en curso.
     * Solo aplicable si la activación está en estado INITIATED o IN_PROGRESS.
     *
     * DELETE /activations/:id
     */
    abandonActivation(activationId: string): Observable<AbandonActivationResponse> {
        const url = `${this.BASE_URL}${ENV_CONFIG.endpoints.activaciones.abandon(activationId)}`;

        return this.http.delete<AbandonActivationResponse>(url, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleError.bind(this))
        );
    }

    // ── Start Workflow ────────────────────────────────────────────────────────

    /**
     * Arranca el workflow post-activación (Step Function).
     * Debe llamarse después de POST /activation/execute exitoso.
     * El permiso requerido es: apactivaciones.start-workflow
     *
     * POST /activations/:id/workflow
     */
    startWorkflow(activationId: string): Observable<StartWorkflowResponse> {
        const url = `${this.BASE_URL}${ENV_CONFIG.endpoints.activaciones.workflow(activationId)}`;

        return this.http.post<StartWorkflowResponse>(url, null, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleError.bind(this))
        );
    }

    // ── Catalog Levels ────────────────────────────────────────────────────────

    /**
     * Obtiene la estructura de niveles del catálogo jerárquico.
     * Ejemplo para CR: Provincia → Cantón → Distrito
     *
     * GET /catalogs/levels?countryCode=CR&catalogType=geographic
     */
    getCatalogLevels(countryCode: string, catalogType: 'geographic' = 'geographic'): Observable<GetCatalogLevelsResponse> {
        const url = `${this.IDENTITY_URL}${ENV_CONFIG.endpoints.activaciones.catalogLevels(countryCode, catalogType)}`;

        return this.http.get<GetCatalogLevelsResponse>(url, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleError.bind(this))
        );
    }

    // ── Catalog Entries ───────────────────────────────────────────────────────

    /**
     * Obtiene las entradas de un nivel del catálogo.
     * Para el nivel raíz, parentId es null.
     * Para niveles hijos, enviar el id de la entrada seleccionada en el nivel padre.
     *
     * POST /catalogs/entries
     */
    getCatalogEntries(request: GetCatalogEntriesRequest): Observable<GetCatalogEntriesResponse> {
        const url = `${this.IDENTITY_URL}${ENV_CONFIG.endpoints.activaciones.catalogEntries}`;

        return this.http.post<GetCatalogEntriesResponse>(url, request, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleError.bind(this))
        );
    }

    // ── Manual Entry ───────────────────────────────────────────────────────────

    /**
     * Envía los datos manuales del documento del cliente.
     *
     * POST /manual-entry
     */
    submitManualEntry(request: ManualEntryRequest): Observable<ManualEntryResponse> {
        const url = `${this.IDENTITY_URL}${ENV_CONFIG.endpoints.activaciones.manualEntry}`;

        return this.http.post<ManualEntryResponse>(url, request, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleManualEntryError.bind(this))
        );
    }

    // ── Max Activations ───────────────────────────────────────────────────────

    /**
     * Valida que el documento del cliente no haya excedido el máximo de líneas
     * activadas permitido por país.
     *
     * - GT / HN / NI → consulta adaptador DPI (fail-closed: si falla, retorna error)
     * - CR / SV       → retorna `{ allowed: true }` directamente sin validación externa
     *
     * Debe llamarse después de la validación gubernamental y con el número dummy
     * de la SIM ya capturado (campo `msisdn`).
     *
     * POST /activations-identity/max-activations
     */
    checkMaxActivations(request: MaxActivationsCheckRequest): Observable<MaxActivationsCheckResponse> {
        const url = `${this.IDENTITY_URL}${ENV_CONFIG.endpoints.activaciones.maxActivations}`;

        return this.http.post<MaxActivationsCheckResponse>(url, request, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleMaxActivationsError.bind(this))
        );
    }

    // ── OCR ───────────────────────────────────────────────────────────────────

    /**
     * Solicita la extracción OCR de los datos del documento capturado.
     * El backend localiza la imagen ya almacenada en S3 a través del `activationId`,
     * por lo que este endpoint NO recibe imágenes — solo metadatos del documento.
     *
     * POST /activations-identity/ocr
     */
    submitOcr(request: OcrRequest): Observable<OcrResponse> {
        const url = `${this.IDENTITY_URL}${ENV_CONFIG.endpoints.activaciones.ocr}`;

        return this.http.post<OcrResponse>(url, request, {
            headers: this.getAuthHeaders()
        }).pipe(
            timeout(TIMEOUT_CONFIG.DEFAULT),
            catchError(this.handleOcrError.bind(this))
        );
    }

    // ── Error Handling ────────────────────────────────────────────────────────

    private handleError(error: HttpErrorResponse): Observable<never> {
        let activacionError: ActivacionErrorResponse;

        this.logger.warn('[ActivacionHttpService] handleError — status:', error.status, '| body:', error.error);

        if (error.error && typeof error.error === 'object' && 'code' in error.error) {
            activacionError = {
                message: error.error.message || this.getDefaultMessage(error.error.code as ActivacionErrorCode),
                code: error.error.code as ActivacionErrorCode
            };
        } else if (error.status === 0) {
            activacionError = {
                message: 'No se pudo conectar con el servidor. Verifique su conexión.',
                code: 'WORKFLOW_START_FAILED'
            };
        } else if (error.status === 401) {
            activacionError = {
                message: 'Sesión expirada. Por favor inicie sesión nuevamente.',
                code: 'WORKFLOW_START_FAILED'
            };
        } else if (error.status === 409) {
            const code: ActivacionErrorCode = error.error?.code ?? 'ACTIVATION_ALREADY_IN_PROGRESS';
            activacionError = {
                message: error.error?.message || this.getDefaultMessage(code),
                code
            };
        } else if (error.status === 404) {
            activacionError = {
                message: 'No se encontró la activación indicada.',
                code: 'ACTIVATION_NOT_FOUND'
            };
        } else {
            activacionError = {
                message: error.message || 'Error inesperado en el servidor.',
                code: 'WORKFLOW_START_FAILED'
            };
        }

        return throwError(() => activacionError);
    }

    /**
     * Manejo de errores específico para manual-entry.
     * Retorna errores con código VALIDATION_ERROR, INVALID_DOCUMENT_FORMAT o INVALID_DOCUMENT_NUMBER.
     */
    private handleManualEntryError(error: HttpErrorResponse): Observable<never> {
        this.logger.warn('[ActivacionHttpService] handleManualEntryError — status:', error.status);
        this.logger.error('[ActivacionHttpService] handleManualEntryError — error body:', error.error);

        if (error.error && typeof error.error === 'object' && 'code' in error.error) {
            const manualError: ManualEntryError = {
                status: error.status,
                code: error.error.code,
                message: error.error.message || this.getManualEntryDefaultMessage(error.error.code),
            };
            return throwError(() => manualError);
        }

        if (error.status === 0) {
            return throwError(() => ({
                status: 0,
                code: 'VALIDATION_ERROR' as const,
                message: 'No se pudo conectar con el servidor.',
            }));
        }

        return throwError(() => ({
            status: error.status,
            code: 'VALIDATION_ERROR' as const,
            message: error.message || 'Error inesperado.',
        }));
    }

    /**
     * Manejo de errores específico para max-activations.
     * - 422 MAX_ACTIVATIONS_EXCEEDED → el documento ya alcanzó el límite
     * - 502 EXTERNAL_SERVICE_ERROR   → adaptador DPI no disponible (fail-closed)
     * - 400 VALIDATION_ERROR         → campo requerido faltante
     */
    private handleMaxActivationsError(error: HttpErrorResponse): Observable<never> {
        this.logger.warn('[ActivacionHttpService] handleMaxActivationsError — status:', error.status);
        this.logger.error('[ActivacionHttpService] handleMaxActivationsError — body:', error.error);

        if (error.error && typeof error.error === 'object' && 'code' in error.error) {
            const maxError: MaxActivationsError = {
                status: error.status,
                code: error.error.code,
                message: error.error.message ?? 'Error en la validación de activaciones.',
            };
            return throwError(() => maxError);
        }

        if (error.status === 0) {
            return throwError(() => ({
                status: 0,
                code: 'VALIDATION_ERROR' as const,
                message: 'No se pudo conectar con el servidor.',
            }));
        }

        return throwError(() => ({
            status: error.status,
            code: 'VALIDATION_ERROR' as const,
            message: error.message ?? 'Error inesperado al validar el máximo de activaciones.',
        }));
    }

    /**
     * Manejo de errores específico para OCR.
     * - 503 CIRCUIT_BREAKER_OPEN → circuito abierto, ofrecer ingreso manual
     * - 502 EXTERNAL_SERVICE_ERROR → servicio OCR externo no disponible
     * - 400 VALIDATION_ERROR → request mal formado
     */
    private handleOcrError(error: HttpErrorResponse): Observable<never> {
        this.logger.warn('[ActivacionHttpService] handleOcrError — status:', error.status);
        this.logger.error('[ActivacionHttpService] handleOcrError — body:', error.error);

        const code: OcrErrorCode =
            error.error?.code ?? this.httpStatusToOcrCode(error.status);

        const ocrError: OcrError = {
            status: error.status,
            code,
            message: error.error?.message ?? this.getOcrDefaultMessage(code, error.status),
        };

        return throwError(() => ocrError);
    }

    private httpStatusToOcrCode(status: number): OcrErrorCode {
        if (status === 503) return 'CIRCUIT_BREAKER_OPEN';
        if (status === 502) return 'EXTERNAL_SERVICE_ERROR';
        if (status === 422) return 'INVALID_DOCUMENT_FORMAT';
        if (status === 404) return 'DOCUMENT_NOT_FOUND';
        return 'VALIDATION_ERROR';
    }

    private getOcrDefaultMessage(code: OcrErrorCode, status?: number): string {
        if (status === 0) return 'No se pudo conectar con el servidor.';
        const messages: Record<OcrErrorCode, string> = {
            CIRCUIT_BREAKER_OPEN: 'El servicio OCR no está disponible temporalmente. Por favor ingrese los datos manualmente.',
            EXTERNAL_SERVICE_ERROR: 'Error en el servicio de reconocimiento de documentos. Por favor ingrese los datos manualmente.',
            INVALID_DOCUMENT_FORMAT: 'La imagen del documento no pudo ser procesada. Por favor ingrese los datos manualmente.',
            DOCUMENT_NOT_FOUND: 'No se encontró el documento capturado. Complete el proceso de captura antes de continuar.',
            VALIDATION_ERROR: 'Error de validación en la solicitud OCR.',
        };
        return messages[code] ?? 'Error inesperado al procesar el documento.';
    }

    private getManualEntryDefaultMessage(code: string): string {        const messages: Record<string, string> = {
            'VALIDATION_ERROR': 'Faltan campos requeridos. Complete todos los campos obligatorios.',
            'INVALID_DOCUMENT_FORMAT': 'El formato del documento no es válido para el tipo seleccionado.',
            'INVALID_DOCUMENT_NUMBER': 'El número de documento no es válido.',
        };
        return messages[code] ?? 'Error de validación.';
    }

    private getDefaultMessage(code: ActivacionErrorCode): string {
        const messages: Record<ActivacionErrorCode, string> = {
            'ACTIVATION_ALREADY_IN_PROGRESS': 'El vendedor ya tiene una activación en curso. Debe completar o abandonar la existente.',
            'ACTIVATION_NOT_FOUND': 'No existe sesión de activación con el ID proporcionado.',
            'ACTIVATION_CANNOT_BE_ABANDONED': 'La activación está en un estado terminal y no puede abandonarse.',
            'POS_BALANCE_UNAVAILABLE': 'No se pudo consultar el saldo POS del vendedor.',
            'WORKFLOW_START_FAILED': 'No se pudo iniciar el flujo de activación. Intente nuevamente.',
            'CONFIG_NOT_FOUND': 'No se encontró configuración para el país indicado.',
            'VALIDATION_ERROR': 'Los datos enviados no son válidos. Verifique la información e intente nuevamente.',
        };
        return messages[code] ?? 'Ha ocurrido un error inesperado.';
    }
}
